import { BehaviorSubject, map } from 'rxjs';
import { WorkflowJanusTableRow } from './workflow-janus-table-row.model';
import { Injectable } from '@angular/core';
import { Workflow, WorkflowConfigurationSaveAction, WorkflowConfigurationSaveListItem } from 'src/app/models';
import { formatDate } from '@angular/common';
import { WorkflowJanusState } from './workflow-janus.state';

@Injectable({ providedIn: 'root' })
export class WorkflowJanusService {
  private newWorkflowBtnDisabledSubject = new BehaviorSubject<boolean>(false);

  newWorkflowBtnDisabled$ =
    this.newWorkflowBtnDisabledSubject.asObservable();

  setNewWorkflowButtonDisabled(disabled: boolean) {
    this.newWorkflowBtnDisabledSubject.next(disabled);
  }

  private stateSource = new BehaviorSubject<WorkflowJanusState>({
    workflows: [],
    archivedWorkflows: [],
    pendingWorkflows: []
  });

  private dataSource = new BehaviorSubject<WorkflowJanusState>({
    searchText: [],
    filterInputs: [],
    attributeFilterInputs: [],
    reRunApplicationTable: false,
    columns: [],
    columnOptions: [],
    exportOptions: [],
    isEditorVisible: false,
    appViewLoading: false,
    isWorkflowValid: true
  });

  private actionSource = new BehaviorSubject<{ action: string; row: any } | null>(null);
  action$ = this.actionSource.asObservable();

  state$ = this.stateSource.asObservable();

  data$ = this.dataSource.asObservable();

  setData(data: WorkflowJanusState): void {
    this.dataSource.next(data);
  }

  getData(): WorkflowJanusState {
    return this.dataSource.value;
  }

  workflows$ = this.state$.pipe(map(s => s.workflows));
  archivedWorkflows$ = this.state$.pipe(map(s => s.archivedWorkflows));
  pendingWorkflows$ = this.state$.pipe(map(s => s.pendingWorkflows));

  private mapToTableRows(workflows: Workflow[]): WorkflowJanusTableRow[] {
    return workflows.map(wf => ({
      name: wf.version?.name ?? '-',
      lastPublished: wf['lastPublishedOn']
        ? formatDate(wf['lastPublishedOn'], 'MM/dd/yyyy, hh:mm a', 'en-IN')
        : '-',
      status: wf.isEnabled
        ?!wf['lastPublishedOn']
          ? 'Never published'
          : wf.canPublish && wf.isEnabled && wf.areThereChangesNotPublished && wf['lastPublishedOn']
            ? 'Changes available'
            : wf['lastPublishedOn']
              ? 'Publish'
              :''
        : 'Disabled',
      actions: wf,
      raw: wf
    }));
  }

  private mapPendingToTableRows(
    items: WorkflowConfigurationSaveListItem[]
  ) {
    return items.map(item => ({
      id: item.id,
      sourceWorkflowName: item.sourceWorkflowName,
      sourceWorkflowId: item.sourceWorkflowId,
      action: item.action,
      createdBy: item.createdBy,
      modifiedBy: item.modifiedBy,
      detailedAction: item.detailedAction,
      raw: item
    }));
  }

  setWorkflows(workflows: Workflow[]) {
    const state = this.stateSource.value;
    this.stateSource.next({
      ...state,
      workflows: this.mapToTableRows(workflows)
    });
  }

  getWorkflows(): Workflow[] {
    return this.stateSource.value.workflows.map(row => row.raw);
  }

  setArchivedWorkflows(workflows: Workflow[]) {
    const state = this.stateSource.value;
    this.stateSource.next({
      ...state,
      archivedWorkflows: this.mapToTableRows(workflows)
    });
  }

  setPendingWorkflows(items: WorkflowConfigurationSaveListItem[]) {
    const state = this.stateSource.value;
    this.stateSource.next({
      ...state,
      pendingWorkflows: this.mapPendingToTableRows(items)
    });
  }

  clear() {
    this.stateSource.next({
      workflows: [],
      archivedWorkflows: [],
      pendingWorkflows: []
    });
  }

  emitAction(event: { action: string; row: any }) {
    this.actionSource.next(event);
  }

  // added by Vipul on 01-04-2026
  setSelectedWorkflow(workflow: Workflow) {
    const state = this.stateSource.value;
    this.stateSource.next({
      ...state,
      selectedWorkflow: workflow
    });
  }

  getSelectedWorkflow(): Workflow | undefined {
    return this.stateSource.value.selectedWorkflow;
  }

  setWorkflow(workflow: Workflow) {
    const state = this.stateSource.value;
    this.stateSource.next({
      ...state,
      subWorkflow: workflow
    });
  }

  getWorkflow(): Workflow | undefined {
    return this.stateSource.value.subWorkflow;
  }
  // end by Vipul on 01-04-2026

  // added by Deepak on 18-04-2026
    private bulkSelectionSubject = new BehaviorSubject<any[]>([]);
    bulkSelection$ = this.bulkSelectionSubject.asObservable();

    setBulkSelection(data: any[]) {
      this.bulkSelectionSubject.next(data);
    }
  // end by Deepak on 18-04-2026

  // start by Vipul on 23-06-2026
  clearAction() {
    this.actionSource.next(null);
  }
  // end by Vipul on 23-06-2026
}
